/**
 * remiseAZero — ce que `reset` de `format_paragraphs` écrit sur un paragraphe,
 * et ce que « remis à zéro » veut dire à la relecture (#1867, spec #1858).
 *
 * Remettre un paragraphe à zéro, c'est supprimer la forme posée à la main — de
 * paragraphe ET de texte — pour que reparaisse celle du style nommé, qui reste.
 * Module pur : l'exécuteur (`formatParagraphsExecutor.ts`) lui demande quoi
 * écrire, la vérification (`verificationDeParagraphe.ts`) comment juger.
 *
 * ## Ce qui est mesuré, et dont ces règles dépendent
 *
 * Sonde `tests/ws-mocked/mesure-format-paragraphs-bordure-reset.spec.ts`
 * (SuperDoc 2.15.1, 2026-10-02 et 2026-10-04, éditeur en mode suggestion,
 * appels en direct) :
 *
 * - **`format.paragraph.resetDirectFormatting` efface tout le `w:pPr` direct,
 *   sauf le style nommé, la numérotation et la section** (Z1, Z4, S1, S2, S5,
 *   S7) : bordure, retraits, espacement, alignement, mais aussi tabulations,
 *   ombrage, enchaînements, saut de page avant, niveau de plan et forme de la
 *   marque de paragraphe (`w:pPr/w:rPr`). Un paragraphe de liste reste une
 *   liste : son retrait redevient celui de son niveau. Une écriture par
 *   paragraphe, une entrée d'historique, aucune étape de plan (Z5).
 * - **Le saut de page avant et le niveau de plan se redonnent ensuite** (R1 à
 *   R3) : `format.paragraph.setFlowOptions({pageBreakBefore})` — allumé comme
 *   éteint contre un style — et `format.paragraph.setOutlineLevel`, une
 *   écriture chacun, aucune révision ; le paragraphe reste en tête de sa page.
 *   `setOutlineLevel` accepte les entiers de 0 à 9 et les écrit tels quels ; il
 *   refuse tout autre nombre (`INVALID_INPUT`, jeté : 10, -1, 1,5) sans rien
 *   écrire (N, 2026-10-04).
 * - **Il ne touche pas la forme du TEXTE** (Z1 : les `w:rPr` des passages sont
 *   intacts) et se dit sans effet (`NO_OP`, rendu) sur un paragraphe sans
 *   `w:pPr` direct, même si son texte porte une forme directe (Z2, Z3, S6).
 * - **La forme directe du texte s'efface par un plan** : une étape
 *   `format.apply` de bloc dont chaque réglage vaut `null` retire l'élément
 *   correspondant de chaque `w:rPr` du paragraphe (T, Y, K : un par un, puis
 *   ensemble). Chaque réglage n'efface que SON élément : `bold` nul laisse
 *   `w:bCs`, la taille nulle laisse `w:szCs` (J1), d'où leurs jumeaux dans la
 *   table. Le style de caractère (`w:rStyle`) reste, sur un lien comme
 *   ailleurs ; une insertion en attente perd sa forme directe et reste une
 *   insertion ; un texte supprimé en attente n'est pas touché, même s'il porte
 *   une forme en attente (X, D1). `highlight` nul efface aussi l'ombrage du
 *   passage (X-OMBRE).
 * - **L'étape de bloc ne touche pas un appel de note de bas de page** : le
 *   passage qui porte `w:footnoteReference` garde son `w:rPr` tel quel — son
 *   exposant posé à la main reste, au fichier comme à l'écran, alors que le
 *   « 1er » en exposant du même paragraphe perd le sien (garde
 *   `canvas-copy-format.spec.ts`, parcours 5, 2026-10-04 : `reset` et
 *   `copy_format`). Le lecteur de forme n'inventorie pas ce passage sans
 *   texte : rien n'en est compté, et rien n'en part.
 * - **`vanish` nul rend visible un texte masqué** (même garde, par mutation :
 *   `vanish` remis dans la table, le passage n'est plus masqué au fichier).
 * - **Une étape qui n'efface rien fait refuser le plan** (`NO_OP`, jeté — T9,
 *   Y-SEPT-SUR-BARRE) : un paragraphe n'entre au plan que s'il porte un élément
 *   que l'étape efface.
 * - **Deux révisions en attente font refuser le plan de texte**, à
 *   l'application et sans rien écrire, alors que l'aperçu l'avait dit valide :
 *   une forme de texte en attente sur un passage visible (`INVALID_CONTEXT:
 *   unsupported-context`, X-FORME-EN-ATTENTE) et un déplacement en attente dans
 *   le paragraphe (`INVALID_CONTEXT: format selection straddles a bookmark,
 *   protection, or move-range marker`, MV3, MV4). Ces deux-là sont connues
 *   d'avance ({@link ecrituresDeRemiseAZero}) ; tout autre refus du plan de
 *   texte — un signet, un contrôle verrouillé — vient de l'éditeur, et le socle
 *   saute alors le paragraphe et le nomme (`ecritureDeForme.ts`, « Quand
 *   l'éditeur refuse un lot »).
 * - **La remise à zéro EFFACE une révision en attente portée par `w:pPr`** :
 *   une forme de paragraphe en attente (`w:pPrChange`, S3), une marque de
 *   paragraphe insérée ou supprimée (M1, M3) — y compris celle d'un paragraphe
 *   que l'éditeur vient d'insérer sous suivi (M7 : le texte reste une
 *   insertion, sa marque ne l'est plus). Elle garde un déplacement (MV1, MV2).
 *   Une bordure, un retrait ou un alignement gardent toutes ces révisions (M2,
 *   M4 à M6).
 *
 * ## Ce qui en découle
 *
 * - Le texte d'abord, par plans atomiques (aperçus avant toute écriture), puis
 *   par paragraphe : la remise à zéro, et aussitôt sa structure redonnée. Un
 *   paragraphe dont l'éditeur refuse l'étape de texte n'est pas remis à zéro
 *   du tout : il est sauté pour tout l'appel. Un
 *   plan ne peut pas suivre une écriture par paragraphe (`ecritureDeForme.ts`,
 *   « Ce que la révision garde »).
 * - **La structure reste** : saut de page avant et niveau de plan ne sont pas
 *   de la forme typographique. « Remets tout l'acte normal » ne fait pas partir
 *   les sauts de page d'un acte.
 * - **Une révision en attente ne bloque que ce qu'elle interdit**
 *   ({@link ecrituresDeRemiseAZero}). Portée par `w:pPr` (forme de paragraphe,
 *   marque) : l'éditeur l'effacerait, le paragraphe n'est PAS remis à zéro — il
 *   est compté et nommé (`format.skipped`, `pending_tracked_change`), pour que l'avocat accepte ou
 *   refuse d'abord cette révision. Portée par le texte (forme de texte,
 *   déplacement) : seul le plan de texte serait refusé ; la forme de
 *   PARAGRAPHE est remise à zéro, la forme de texte est laissée et le reçu
 *   nomme le paragraphe (`format.reset.textFormattingLeft`). S'il n'a que de
 *   la forme de texte à retirer, il est laissé entier.
 * - **Un niveau de plan que l'éditeur ne saurait pas redonner** (hors des
 *   entiers 0 à 9) fait laisser le paragraphe d'avance
 *   (`outline_level_not_restorable`) : le remettre à zéro le ferait perdre.
 * - La langue (`w:lang`) et le style de caractère ne sont pas de la forme posée
 *   à la main : ils restent. Le masquage d'un texte, son sens d'écriture, sa
 *   grille non plus ({@link HORS_FORME_DE_CARACTERE}) : « remets ce paragraphe
 *   normal » ne révèle pas un texte masqué.
 * - **La bordure part avec le reste**, barre de changement comprise (la
 *   génération d'acte la pose en `w:pBdr` direct). Ce que la remise à zéro
 *   retire au-delà des réglages des outils est COMPTÉ par catégorie
 *   (`format.reset.removed`), sur la foi de la RELECTURE : un élément encore
 *   au fichier n'est pas compté parti, il est nommé (`format.reset.notRemoved`).
 *   Ce que l'éditeur ne sait pas retirer du texte y est nommé aussi, et ne
 *   rend pas le paragraphe « différent » — redemander n'y changerait rien.
 */

import { CanvasFormatResetRemovedSchema, type CanvasFormatResetRemoved, type CanvasFormatWriteReceipt, type CanvasParagraphFormat } from '@hector/shared-types';
import { reglagesDeParagrapheARendre } from './formePourLAgent';
import type { FormeDeParagraphe } from '@hector/docx-forme';
import type { RelectureDeLActe } from './verificationDeForme';

/**
 * L'élément de `w:rPr` que chaque réglage nul efface. Chaque couple est mesuré
 * (gestes T, Y, J et K de la sonde) ; les sept premiers sont les réglages de
 * `format_text`.
 */
export const REGLAGE_QUI_EFFACE: Readonly<Record<string, string>> = {
  b: 'bold',
  i: 'italic',
  u: 'underline',
  highlight: 'highlight',
  color: 'color',
  rFonts: 'fontFamily',
  sz: 'fontSize',
  bCs: 'bCs',
  iCs: 'iCs',
  szCs: 'fontSizeCs',
  strike: 'strike',
  dstrike: 'dstrike',
  caps: 'caps',
  smallCaps: 'smallCaps',
  vertAlign: 'vertAlign',
  shd: 'shading',
  spacing: 'letterSpacing',
  position: 'position',
  bdr: 'border',
  w: 'charScale',
  kern: 'kerning',
  outline: 'outline',
  shadow: 'shadow',
  emboss: 'emboss',
  imprint: 'imprint',
  em: 'em',
};

/**
 * Les éléments de `w:rPr` posés à la main qui ne sont PAS de la forme au sens
 * de l'avocat : ils touchent au contenu ou à sa composition, pas à l'apparence
 * typographique. Ni une remise à zéro ni une copie de forme ne les retirent,
 * ne les jugent, ni ne les nomment « non retirés » — l'éditeur saurait les
 * effacer (`vanish: null`… mesuré, geste K), et c'est bien pourquoi ils sont
 * tenus hors de {@link REGLAGE_QUI_EFFACE} :
 *
 * - le masquage (`vanish`, `webHidden`, `specVanish`) : le retirer RÉVÈLE un
 *   texte que l'auteur a caché ;
 * - le sens d'écriture et l'écriture complexe (`rtl`, `cs`) ;
 * - la grille (`snapToGrid`), l'ajustement à une largeur (`fitText`) et la mise
 *   en page asiatique (`eastAsianLayout`).
 *
 * Seul endroit où cet ensemble est dit : la copie (`copieDeForme.ts`) le lit ici.
 */
export const HORS_FORME_DE_CARACTERE: ReadonlySet<string> = new Set(['vanish', 'webHidden', 'specVanish', 'rtl', 'cs', 'snapToGrid', 'fitText', 'eastAsianLayout']);

/**
 * Les éléments de texte des sept réglages de `format_text` et de leurs jumeaux
 * d'écriture complexe, que Word écrit avec eux : les retirer n'est pas une
 * perte à compter à part.
 */
const ELEMENTS_DES_REGLAGES_DE_TEXTE: ReadonlySet<string> = new Set(['b', 'i', 'u', 'highlight', 'color', 'rFonts', 'sz', 'bCs', 'iCs', 'szCs']);

/** Les éléments de `w:pPr` que les réglages de `format_paragraphs` posent, hors la bordure : les retirer n'est pas une perte à compter. */
const ELEMENTS_DES_REGLAGES_DE_PARAGRAPHE: ReadonlySet<string> = new Set(['jc', 'ind', 'spacing']);

/** La catégorie de perte de chaque élément de `w:pPr` qu'une remise à zéro retire ; tout autre élément est « autre forme de paragraphe ». */
const PERTE_DE_PARAGRAPHE: Readonly<Record<string, CanvasFormatResetRemoved>> = {
  pBdr: 'border',
  tabs: 'tabs',
  shd: 'shading',
  keepNext: 'keepWithNext',
  keepLines: 'keepLines',
  widowControl: 'widowControl',
  contextualSpacing: 'contextualSpacing',
  framePr: 'frame',
  rPr: 'paragraphMark',
};

/** L'`inline` de l'étape de plan qui retire la forme directe du texte d'un paragraphe. */
export const INLINE_DE_REMISE_A_ZERO: Readonly<Record<string, null>> = Object.fromEntries(Object.values(REGLAGE_QUI_EFFACE).map((reglage) => [reglage, null]));

/** Pourquoi un paragraphe qui porte de la forme à la main n'est pas remis à zéro. */
export type RaisonDeLaisser = 'pending_tracked_change' | 'outline_level_not_restorable';

/** Ce qu'une remise à zéro doit écrire sur un paragraphe, d'après sa forme lue AVANT l'écriture. */
export interface EcrituresDeRemiseAZero {
  /** Vrai si le texte porte une forme directe que l'étape de plan efface, et que rien n'interdit de l'écrire. */
  texte: boolean;
  /** Vrai si le `w:pPr` direct porte de la forme posée à la main. */
  paragraphe: boolean;
  /**
   * La structure à redonner après la remise à zéro du paragraphe, qui
   * l'efface : le saut de page avant et le niveau de plan posés à la main.
   * Vide quand le paragraphe n'est pas remis à zéro, ou n'en porte pas.
   */
  structure: FormeDeParagraphe['formeDirecte']['structure'];
  /** Présent : le paragraphe porte de la forme à la main et n'est PAS écrit du tout, pour cette raison. */
  laisse?: RaisonDeLaisser;
  /**
   * Vrai quand la forme du paragraphe est remise à zéro mais que celle de son
   * TEXTE est laissée (`texte` est alors faux) : une révision en attente portée
   * par le texte ferait refuser le plan.
   */
  texteLaisse?: true;
}

/** Vrai si l'éditeur sait redonner ce niveau de plan (mesure N : les entiers de 0 à 9) ; un niveau absent n'est pas à redonner. */
const niveauRedonnable = (niveau: number | undefined): boolean => niveau === undefined || (Number.isInteger(niveau) && niveau >= 0 && niveau <= 9);

/**
 * Ce qu'il y a à écrire pour remettre ce paragraphe à zéro ; rien : il montre
 * déjà la forme de son style. Une révision en attente ou un niveau de plan
 * hors bornes ne retirent de l'écriture que ce qu'ils interdisent (en-tête,
 * « Ce qui en découle »).
 */
export function ecrituresDeRemiseAZero(paragraphe: FormeDeParagraphe): EcrituresDeRemiseAZero {
  const { deParagraphe, deTexte, structure, revisionEnAttente } = paragraphe.formeDirecte;
  const duParagraphe = deParagraphe.length > 0;
  const duTexte = deTexte.some((element) => element in REGLAGE_QUI_EFFACE);
  const prevu = { texte: duTexte, paragraphe: duParagraphe, structure: duParagraphe ? structure : {} };
  if (!duTexte && !duParagraphe) return prevu;
  // L'éditeur effacerait la révision portée par `w:pPr`.
  if (revisionEnAttente.deParagraphe) return { ...prevu, laisse: 'pending_tracked_change' };
  if (duParagraphe && !niveauRedonnable(structure.niveauDePlan)) return { ...prevu, laisse: 'outline_level_not_restorable' };
  if (revisionEnAttente.deTexte && duTexte) {
    // Le plan de texte serait refusé ; la remise à zéro du paragraphe, elle, garde la révision.
    return duParagraphe ? { ...prevu, texte: false, texteLaisse: true } : { ...prevu, laisse: 'pending_tracked_change' };
  }
  return prevu;
}

/**
 * Ce qu'un paragraphe porte encore à la main et qu'une remise à zéro retire,
 * par le nom des éléments du fichier (`w:` omis) : ceux de son `w:pPr`, puis
 * ceux de son texte que l'éditeur sait effacer. Vide : il est remis à zéro.
 *
 * @param texteLaisse Vrai pour un paragraphe dont la forme de texte a été
 *   laissée exprès : seule sa forme de paragraphe est jugée.
 */
export function resteALaMain(paragraphe: FormeDeParagraphe, texteLaisse = false): string[] {
  return [
    ...paragraphe.formeDirecte.deParagraphe.map((element) => `pPr/${element}`),
    ...(texteLaisse ? [] : paragraphe.formeDirecte.deTexte.filter((element) => element in REGLAGE_QUI_EFFACE).map((element) => `rPr/${element}`)),
  ];
}

/** Les éléments de texte posés à la main que l'éditeur ne sait pas retirer (aucun réglage mesuré ne les efface) : ils restent. */
export function nonRetirables(paragraphe: FormeDeParagraphe): string[] {
  return paragraphe.formeDirecte.deTexte.filter((element) => !(element in REGLAGE_QUI_EFFACE) && !HORS_FORME_DE_CARACTERE.has(element)).map((element) => `rPr/${element}`);
}

/**
 * Ce qu'une remise à zéro retire de ce paragraphe au-delà des réglages des
 * outils de forme, d'après sa forme lue AVANT l'écriture : chaque élément du
 * fichier (`pPr/…`, `rPr/…`) avec sa catégorie.
 *
 * @param texteLaisse Vrai quand la forme de texte du paragraphe n'est pas écrite : rien n'en part.
 */
export function pertesDeLaRemiseAZero(paragraphe: FormeDeParagraphe, texteLaisse = false): Array<{ element: string; categorie: CanvasFormatResetRemoved }> {
  const duParagraphe = paragraphe.formeDirecte.deParagraphe
    .filter((element) => !ELEMENTS_DES_REGLAGES_DE_PARAGRAPHE.has(element))
    .map((element) => ({ element: `pPr/${element}`, categorie: PERTE_DE_PARAGRAPHE[element] ?? 'otherParagraphFormatting' }));
  const duTexte = texteLaisse ? [] : paragraphe.formeDirecte.deTexte
    .filter((element) => element in REGLAGE_QUI_EFFACE && !ELEMENTS_DES_REGLAGES_DE_TEXTE.has(element))
    .map((element) => ({ element: `rPr/${element}`, categorie: 'otherTextFormatting' as const }));
  return [...duParagraphe, ...duTexte];
}

/** Le bilan d'une remise à zéro, tel que le reçu le rend (`format.reset.removed`, `format.reset.notRemoved`). */
export type BilanDeRemiseAZero = Pick<NonNullable<CanvasFormatWriteReceipt['reset']>, 'removed' | 'notRemoved'>;

/**
 * Compte ce que la remise à zéro a retiré au-delà des réglages des outils, et
 * nomme ce qui reste. Une perte n'est comptée que si la RELECTURE la confirme :
 * l'élément lu avant l'écriture n'est plus au fichier relu. Un élément encore
 * là — retrait raté, ou que l'éditeur ne sait pas retirer — est nommé dans
 * `notRemoved`. Sans relecture, rien n'est dit parti.
 *
 * @param ecrits Les paragraphes entièrement écrits, tels que lus AVANT l'écriture.
 * @param nonEcritsDuTexte Les paragraphes de la cible lus AVANT l'écriture, écrits ou non : ce que l'éditeur ne sait pas retirer de leur texte y reste.
 */
export function bilanDeLaRemiseAZero(
  ecrits: ReadonlyArray<{ avant: FormeDeParagraphe; texteLaisse: boolean }>,
  nonEcritsDuTexte: readonly FormeDeParagraphe[],
  relecture: RelectureDeLActe,
): BilanDeRemiseAZero {
  const relus = 'acte' in relecture ? new Map(relecture.acte.paragraphes.map((lu) => [lu.nodeId, lu])) : undefined;
  const partis = new Map<CanvasFormatResetRemoved, number>();
  const restes = new Map<string, number>();
  const compter = <Cle>(comptes: Map<Cle, number>, cles: Iterable<Cle>): void => {
    for (const cle of new Set(cles)) comptes.set(cle, (comptes.get(cle) ?? 0) + 1);
  };
  for (const { avant, texteLaisse } of ecrits) {
    const relu = relus?.get(avant.nodeId);
    if (relu === undefined) continue;
    const encoreLa = new Set([...relu.formeDirecte.deParagraphe.map((element) => `pPr/${element}`), ...relu.formeDirecte.deTexte.map((element) => `rPr/${element}`)]);
    const pertes = pertesDeLaRemiseAZero(avant, texteLaisse);
    compter(partis, pertes.filter((perte) => !encoreLa.has(perte.element)).map((perte) => perte.categorie));
    compter(restes, pertes.filter((perte) => encoreLa.has(perte.element)).map((perte) => perte.element));
  }
  for (const paragraphe of nonEcritsDuTexte) compter(restes, nonRetirables(paragraphe));
  const [premierParti, ...autresPartis] = CanvasFormatResetRemovedSchema.options.flatMap((what) => (partis.has(what) ? [{ what, paragraphs: partis.get(what) ?? 0 }] : []));
  const [premierReste, ...autresRestes] = [...restes].map(([element, paragraphs]) => ({ element, paragraphs }));
  return {
    ...(premierParti === undefined ? {} : { removed: [premierParti, ...autresPartis] }),
    ...(premierReste === undefined ? {} : { notRemoved: [premierReste, ...autresRestes] }),
  };
}

/** La forme de paragraphe qu'un paragraphe remis à zéro montre : les réglages qui se lisent. */
export function formeRendue(paragraphe: FormeDeParagraphe): Partial<CanvasParagraphFormat> {
  const forme: Record<string, unknown> = {};
  for (const reglage of reglagesDeParagrapheARendre(paragraphe.paragraphe)) {
    const lu = paragraphe.paragraphe[reglage];
    if (lu.lisible) forme[reglage] = lu.valeur;
  }
  return forme;
}
